import React, { useContext, useState } from "react";
import isEqual from "lodash/isEqual";
import styled from "styled-components";
import { Alert, Button, Card, InputGroup } from "@blueprintjs/core";
import { faAws } from "@fortawesome/free-brands-svg-icons";
import {
  faEnvelope,
  faExclamationTriangle,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AppToaster, MenuMultiSelect } from "@components";
import {
  useCreateAction,
  useDeleteAction,
  usePolicies,
  useUpdateAction,
} from "@hooks";
import { OrgContext } from "@providers";
import { Feature } from "@utils";

import { PolicyWithRules } from "@pages/policy/types";
import { testAction } from "@pages/settings/api";
import {
  Action,
  ActionChannel,
  ActionConfig,
  EmailConfig,
  WebhookActionChannels,
  WebhookConfig,
} from "@pages/settings/types";

const CONFIG = {
  [ActionChannel.S3]: {
    name: "S3",
    icon: faAws,
  },
  [ActionChannel.Webhook]: {
    name: "Webhook",
    configLabel: "Webhook URL",
    configPlaceholder: "Webhook URL...",
    configType: "url",
  },
  [ActionChannel.SlackWebhook]: {
    name: "Slack Webhook",
    configLabel: "Webhook URL",
    configPlaceholder: "Webook URL...",
    configType: "url",
  },
  [ActionChannel.Email]: {
    icon: faEnvelope,
    name: "Email",
    configLabel: "Email",
    configPlaceholder: "Email address...",
    configType: "email",
  },
};

const ActionCard = styled(Card)`
  padding: 1rem;
  &:not(:first-child) {
    margin-top: 1.2rem;
  }
`;

const ActionLabel = styled.span`
  min-width: 6em;
`;

const InputLabel = styled.span`
  color: #777;
  min-width: 6em;
`;

const WideInput = styled(InputGroup)`
  width: 100%;
  flex-grow: 1;
  display: flex;
`;

const ActionContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ActionHeader = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: none;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.7em;

  & > :not(:first-child) {
    margin-left: 1em;
  }

  & {
    white-space: nowrap;
  }
`;

const ActionHeaderLeft = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: none;
  flex-grow: 1;
  align-items: center;

  & > :not(:first-child) {
    margin-left: 1em;
  }
`;

type Props = {
  inheritedAction: Action;
};

function readConfig(action: Action): string {
  if (action.channel === ActionChannel.S3) {
    return "";
  } else if (WebhookActionChannels.includes(action.channel)) {
    const config = action.config as WebhookConfig;
    return config["url"];
  } else {
    const config = action.config as EmailConfig;
    return config["email"];
  }
}

function createConfig(
  channel: ActionChannel,
  config_field: string
): ActionConfig {
  if (channel === ActionChannel.S3) {
    return {
      url: config_field,
    };
  } else if (WebhookActionChannels.includes(channel)) {
    return {
      url: config_field,
    };
  } else {
    return {
      email: config_field,
    };
  }
}

export const ActionPanelCard: React.FC<Props> = ({ inheritedAction }) => {
  const [action, setAction] = useState<Action>(inheritedAction);
  const [showDeleteWarning, setShowDeleteWarning] = useState<boolean>(false);
  const { org } = useContext(OrgContext);
  const policiesQuery = usePolicies();
  const policies = policiesQuery.data?.policies ?? [];
  const createActionMutation = useCreateAction();
  const updateActionMutation = useUpdateAction();
  const deleteActionMutation = useDeleteAction();

  const saveAction = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (action.id) {
      updateActionMutation.mutate(action);
    } else {
      createActionMutation.mutate(action);
    }
  };

  const deleteAction = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    deleteActionMutation.mutate(action);
  };

  const saveAndTestAction = async (event: any) => {
    event.preventDefault();
    if (action.id) {
      await updateActionMutation.mutateAsync(action);
      if (!updateActionMutation.isSuccess) {
        return;
      }
    } else {
      await createActionMutation.mutateAsync(action);
      if (!createActionMutation.isSuccess) {
        return;
      }
    }

    testAction(org!.id, action.id!).then((response) => {
      if (response.success) {
        AppToaster.show({
          message: `Hook test succeeded`,
          intent: "success",
        });
      } else {
        AppToaster.show({
          message: `Hook test failed`,
          intent: "danger",
        });
      }
    });
  };

  const actionConfig = CONFIG[action.channel];

  const policyIsSelected = (policy: PolicyWithRules) =>
    action.policy_slugs!.some((slug: string) => slug === policy.slug);

  const actionHasUnsavedChanges = () => {
    return (
      action.name !== inheritedAction.name ||
      readConfig(action) !== readConfig(inheritedAction) ||
      !isEqual(action.policy_slugs, inheritedAction.policy_slugs)
    );
  };

  const card = (
    <ActionContent>
      <ActionHeader>
        <ActionHeaderLeft>
          <ActionLabel>
            {"icon" in actionConfig ? (
              <>
                <FontAwesomeIcon icon={actionConfig.icon} /> &nbsp;
              </>
            ) : null}
            {actionConfig.name}
          </ActionLabel>
        </ActionHeaderLeft>
        <Button
          intent="danger"
          minimal
          small
          onClick={() => setShowDeleteWarning(true)}
        >
          Remove integration
        </Button>
      </ActionHeader>
      <form onSubmit={saveAction}>
        <ActionHeader>
          <InputLabel>Name</InputLabel>
          <WideInput
            placeholder="Channel name..."
            value={action.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setAction({ ...action, name: e.target.value });
            }}
          />
          {action.channel === ActionChannel.S3 ? (
            <ActionHeaderLeft>
              <Button onClick={saveAndTestAction}>Test</Button>
              <Button
                type="submit"
                intent="primary"
                disabled={!actionHasUnsavedChanges()}
              >
                Save
              </Button>
            </ActionHeaderLeft>
          ) : null}
        </ActionHeader>
        {"configType" in actionConfig ? (
          <ActionHeader>
            <ActionHeaderLeft>
              <InputLabel>{actionConfig.configLabel}</InputLabel>
              <WideInput
                placeholder={actionConfig.configPlaceholder}
                type={actionConfig.configType}
                value={readConfig(action) ?? ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setAction({
                    ...action,
                    config: createConfig(action.channel, e.target.value),
                  });
                }}
                fill={false}
                className="fs-mask dd-privacy-mask"
              />
              {WebhookActionChannels.includes(action.channel) ? (
                <Button onClick={saveAndTestAction}>Test</Button>
              ) : null}
            </ActionHeaderLeft>
            <Button
              type="submit"
              intent="primary"
              disabled={!actionHasUnsavedChanges()}
            >
              Save
            </Button>
          </ActionHeader>
        ) : null}
        {!Feature.isNolicy({ org }) && (
          <ActionHeader>
            <ActionHeaderLeft>
              <InputLabel>Policies</InputLabel>
              <MenuMultiSelect<PolicyWithRules>
                className="policymultiselect"
                placeholder="Add..."
                items={policies} // list of items in popover menu
                isItemSelected={policyIsSelected} // whether item in menu is selected
                extractor={(p) => p?.name} // fxn to convert policy to what is displayed
                onItemSelect={(p: PolicyWithRules) => {
                  const currentPolicies = action
                    ? [...action.policy_slugs]
                    : [];
                  let newPolicies;
                  if (currentPolicies.includes(p.slug)) {
                    newPolicies = currentPolicies.filter(
                      (slug) => slug !== p.slug
                    );
                  } else {
                    newPolicies = [...currentPolicies, p.slug];
                  }
                  setAction({
                    ...action,
                    policy_slugs: newPolicies,
                  });
                }}
                onRemove={(p: PolicyWithRules) => {
                  setAction({
                    ...action,
                    policy_slugs: action.policy_slugs.filter(
                      (slug) => slug !== p.slug
                    ),
                  });
                }}
                selectedItems={policies.filter(policyIsSelected)} // items in text box
                popoverProps={{
                  minimal: true,
                }}
              />
              {action.policy_slugs.length === 0 && (
                <span>
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    style={{
                      color: "var(--global-warning-color)",
                      marginRight: "8px",
                    }}
                  />
                  Select one or more policies to use this integration
                </span>
              )}
            </ActionHeaderLeft>
          </ActionHeader>
        )}
      </form>
    </ActionContent>
  );

  return (
    <>
      <Alert
        className="deleteIntegrationWarning"
        cancelButtonText="Cancel"
        confirmButtonText="Remove"
        icon="trash"
        style={{ width: "400px" }}
        intent="danger"
        isOpen={showDeleteWarning}
        onCancel={() => setShowDeleteWarning(false)}
        onConfirm={deleteAction}
      >
        <p>
          Are you sure you want to remove{" "}
          {!action.name ? "this integration" : <b>{action.name}</b>}?
        </p>
        <p>
          Choosing "Remove" will remove this integration from all policies that
          currently reference it.
        </p>
      </Alert>
      {/* <Prompt
        when={actionHasUnsavedChanges()}
        message="You have unsaved changes, are you sure you want to leave?"
      /> */}
      <ActionCard className="notification-option">{card}</ActionCard>
    </>
  );
};
